import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function MasterInventory({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadInventory = async () => {
    setLoading(true);
    setInventoryData([]);

    try {
      // Query EMS Station table
      const { data, error } = await supabase
        .from('ems_station')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        setInventoryData([]);
        return;
      }

      // Define all medication fields
      const medicationFields = [
        'fentanyl',
        'ketamine',
        'etomidate',
        'midazolam10mg_2ml',
        'midazolam5mg_5ml'
      ];

      // Find most recent COMPLETE entry for each medication
      const latestMedications = {};

      data.forEach(record => {
        const recordDate = new Date(record.created_at);

        // Build medications object
        const medications = {};
        let isComplete = true;

        medicationFields.forEach(field => {
          const value = parseFloat(record[field]);
          if (isNaN(value) || value === null || record[field] === null) {
            isComplete = false;
          }
          medications[field] = value;
        });

        // Only consider complete entries (all 10 fields have valid values)
        if (isComplete) {
          medicationFields.forEach(med => {
            if (!latestMedications[med] || recordDate > latestMedications[med].date) {
              latestMedications[med] = {
                quantity: medications[med],
                date: recordDate
              };
            }
          });
        }
      });

      // Format for display (sorted alphabetically)
      const formattedData = Object.keys(latestMedications)
        .map(medKey => ({
          name: formatMedicationName(medKey),
          quantity: latestMedications[medKey].quantity
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setInventoryData(formattedData);
    } catch (error) {
      console.error('Error loading master inventory:', error);
      alert('Error loading inventory data.');
    } finally {
      setLoading(false);
    }
  };

  // Format medication name for display
  const formatMedicationName = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/(\w+)/g, word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );
  };

  useEffect(() => {
    if (isAdmin) {
      loadInventory();
    }
  }, [isAdmin]);

  // Admin access control
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div style={{ padding: '80px 20px 20px', maxWidth: '1000px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          style={{
            padding: '8px 16px',
            border: '2px solid #0E3293',
            borderRadius: '6px',
            background: 'white',
            color: '#0E3293',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back to Admin
        </button>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0E3293 0%, #1A4DBB 100%)',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '30px',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(14, 50, 147, 0.15)'
        }}>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>Master Inventory</h1>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            Latest total quantities of medications from the most recent complete EMS Station inventory log
          </p>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#0E3293' }}>
            <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading Report...</div>
            <div style={{ fontSize: '14px', color: '#666' }}>This may take up to 2 minutes</div>
          </div>
        )}

        {/* Table */}
        {!loading && (
          <>
            <div className="report-table-container">
              <div className="table-scroll">
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#fff',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <thead>
                    <tr style={{ background: '#0E3293', color: '#fff' }}>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600' }}>
                        Medication
                      </th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600' }}>
                        Quantity on Hand
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryData.length === 0 ? (
                      <tr>
                        <td colSpan="2" style={{
                          textAlign: 'center',
                          padding: '40px',
                          color: '#666'
                        }}>
                          No complete inventory data found. A complete entry requires all 5 medication fields to have values.
                        </td>
                      </tr>
                    ) : (
                      inventoryData.map((item, index) => (
                        <tr key={index} style={{
                          borderBottom: '1px solid #e0e0e0',
                          transition: 'background 0.2s'
                        }}>
                          <td style={{ padding: '12px 15px' }}>{item.name}</td>
                          <td style={{ padding: '12px 15px', fontWeight: '600' }}>
                            {item.quantity}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Refresh Button */}
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <button
                onClick={loadInventory}
                className="btn btn-primary"
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  background: '#0E3293',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => e.target.style.background = '#1A4DBB'}
                onMouseOut={(e) => e.target.style.background = '#0E3293'}
              >
                Refresh Data
              </button>
            </div>
          </>
        )}
      </div>

      <footer className="footer">
        <p className="text-small">
          Master Inventory Report - NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC
        </p>
      </footer>
    </div>
  );
}

export default MasterInventory;
